import RegularText from '@/components/RegularText';
import { AppPricingPlan } from '@/constants/enum';
import { capitalizeFirstLetter } from '@/helpers/string';
import userPlans from '@/hooks/userPlans';
import { Banner, BlockStack, Icon, InlineStack, List, Modal, Text } from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
import React from 'react';
import { useSelector } from 'react-redux';
import { pricingSelector } from '@/redux/slice/pricing.slice';
import { startCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { PATH } from '@/constants/path';
import { Trans, useTranslation } from 'react-i18next';

interface DowngradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const DowngradeModal: React.FC<DowngradeModalProps> = ({ isOpen, onClose, onConfirm, isLoading }) => {
  const { appPlanCurrent } = userPlans();
  const pricingSelected = useSelector(pricingSelector);
  const navigate = useNavigate();
  const { t } = useTranslation('pricing');

  const planSelected = capitalizeFirstLetter(pricingSelected.plan);

  const getDowngradeDescription = () => {
    const isDowngradingFromPremium = appPlanCurrent === AppPricingPlan.Paid;

    const isSelectedPlanFree = pricingSelected.plan === AppPricingPlan.Free;
    const isSelectedPlanBasic = pricingSelected.plan === AppPricingPlan.Paid;

    if (isDowngradingFromPremium && isSelectedPlanBasic) {
      return (
        <List type="bullet">
          <List.Item>{t('unlimited_popups_advanced_customization')}</List.Item>
          <List.Item>{t('unlimited_translated_languages')}</List.Item>
          <List.Item>{t('terms_conditions_product_cart_page')}</List.Item>
          <List.Item>{t('live_chat_support_vip_priority')}</List.Item>
        </List>
      );
    }

    if (isDowngradingFromPremium && isSelectedPlanFree) {
      return (
        <List type="bullet">
          <List.Item>{t('unlimited_popups_advanced_customization')}</List.Item>
          <List.Item>{t('show_pop_up_specific_pages')}</List.Item>
          <List.Item>{t('info_banner')}</List.Item>
          <List.Item>{t('unlimited_translated_languages')}</List.Item>
          <List.Item>{t('terms_conditions_product_cart_page')}</List.Item>
          <List.Item>{t('live_chat_support_vip_priority')}</List.Item>
        </List>
      );
    }

    return <></>;
  };
  const description = getDowngradeDescription();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={
        <div style={{ color: '#C81717' }}>
          <InlineStack gap="050">
            <Icon source={AlertTriangleIcon}></Icon>
            {t('downgrade_plan')}
          </InlineStack>
        </div>
      }
      primaryAction={{
        content: t('stay_on_current_plan'),
        onAction: onClose,
      }}
      secondaryActions={[
        {
          loading: isLoading,
          content: t('confirm_downgrade'),
          onAction: onConfirm,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <RegularText>
            <Text as="h3">
              <Trans
                t={t}
                i18nKey="downgrading_removes_access_to_plan_features"
                values={{ plan: startCase(appPlanCurrent) }}
                component={{ strong: <b /> }}
              />
            </Text>

            {description}
          </RegularText>

          <Banner tone="warning" action={{ content: t('review_settings'), onAction: () => navigate(PATH.POP_UP_CUSTOMIZATION) }}>
            <Trans t={t} i18nKey="downgrade_plan_data_disabled" values={{ planName: startCase(planSelected) }} />
          </Banner>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default DowngradeModal;
